import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AdminManagement from './AdminManagement';
import FundraiserManagement from './FundraiserManagement';

const AdminPage = ({ pendingApplicants, onApprove }) => {
  const { isAuthenticated, isLoading, admin, logout, makeAuthenticatedRequest } = useAuth();
  const [activeTab, setActiveTab] = useState('applications');
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [localPendingApplicants, setLocalPendingApplicants] = useState([]);

  // Fetch pending applicants when admin is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchPendingApplicants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const fetchPendingApplicants = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';
      const response = await makeAuthenticatedRequest(`${apiUrl}/pending-applicants`);
      if (response.ok) {
        const data = await response.json();
        setLocalPendingApplicants(data);
      }
    } catch (error) {
      console.error('Error fetching pending applicants:', error);
    }
  };

  const handleApprove = async (applicantId) => {
    if (window.confirm('Are you sure you want to approve this applicant? This will create their fundraising page and make it visible to the public.')) {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';
        const response = await makeAuthenticatedRequest(`${apiUrl}/approve-applicant/${applicantId}`, {
          method: 'POST',
        });
        
        if (response.ok) {
          // Refresh the list after approval
          await fetchPendingApplicants();
          // Also call the parent's onApprove for any additional handling
          onApprove(applicantId);
        }
      } catch (error) {
        console.error('Error approving applicant:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">🔄</div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleMigration = async () => {
    if (!window.confirm('This will migrate your localStorage data to the backend server. Current backend data will be backed up. Continue?')) {
      return;
    }

    setIsMigrating(true);
    setMigrationResult(null);

    try {
      // Get localStorage data
      const localFundraisers = localStorage.getItem('costa-trip-fundraisers');
      const localPending = localStorage.getItem('costa-trip-pending');
      
      const fundraisers = localFundraisers ? JSON.parse(localFundraisers) : [];
      const pendingApplicants = localPending ? JSON.parse(localPending) : [];

      if (fundraisers.length === 0 && pendingApplicants.length === 0) {
        setMigrationResult({
          success: false,
          message: 'No localStorage data found to migrate.'
        });
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';
      const response = await makeAuthenticatedRequest(`${apiUrl}/migrate-data`, {
        method: 'POST',
        body: JSON.stringify({
          fundraisers,
          pendingApplicants
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setMigrationResult({
          success: true,
          message: `Migration successful! Added ${result.fundraisersAdded} fundraisers and ${result.pendingAdded} pending applicants.`,
          details: result
        });

        // Clear localStorage after successful migration
        if (window.confirm('Migration successful! Would you like to clear the localStorage data now that it\'s been migrated to the backend?')) {
          localStorage.removeItem('costa-trip-fundraisers');
          localStorage.removeItem('costa-trip-pending');
        }
      } else {
        const error = await response.json();
        setMigrationResult({
          success: false,
          message: `Migration failed: ${error.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      setMigrationResult({
        success: false,
        message: `Migration failed: ${error.message}`
      });
    } finally {
      setIsMigrating(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Link to="/" className="text-blue-600 hover:text-blue-800 mb-6 inline-block">
        ← Back to home
      </Link>

      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Admin Panel</h1>
              <p className="text-gray-600">
                Review and approve pending applications for the Costa Rica missions team.
              </p>
              <p className="text-sm text-green-600 mt-1">
                ✅ Logged in as: {admin?.username}
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleMigration}
                disabled={isMigrating}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isMigrating ? 'Migrating...' : '📦 Migrate Data'}
              </button>
              <button
                onClick={logout}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                🚪 Logout
              </button>
            </div>
          </div>

          {migrationResult && (
            <div className={`mb-6 p-4 rounded-lg ${migrationResult.success ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
              <h3 className="font-semibold mb-2">
                {migrationResult.success ? '✅ Migration Successful' : '❌ Migration Failed'}
              </h3>
              <p>{migrationResult.message}</p>
              {migrationResult.details && (
                <div className="mt-2 text-sm">
                  <p>Backup created: {migrationResult.details.backup}</p>
                </div>
              )}
            </div>
          )}

          {/* Tab Navigation */}
          <div className="flex border-b mb-6">
            <button
              onClick={() => setActiveTab('applications')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'applications'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              📋 Pending Applications ({localPendingApplicants.length})
            </button>
            <button
              onClick={() => setActiveTab('fundraisers')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'fundraisers'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              💰 Manage Fundraisers
            </button>
            <button
              onClick={() => setActiveTab('admins')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'admins'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              👥 Manage Admins
            </button>
          </div>

          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <>
              {localPendingApplicants.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📋</div>
              <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Pending Applications</h2>
              <p className="text-gray-500 mb-6">
                All applications have been reviewed. New applications will appear here for approval.
              </p>
              <div className="text-sm text-gray-400">
                <p>Applications are submitted through the "Join Team" page.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between border-b pb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Pending Applications ({localPendingApplicants.length})
                </h2>
                <div className="text-sm text-gray-500">
                  Review each application carefully before approving
                </div>
              </div>

              {localPendingApplicants.map(applicant => (
                <div key={applicant.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex flex-col lg:flex-row gap-6">
                    <div className="lg:w-32 flex-shrink-0">
                      {applicant.photo ? (
                        <img 
                          src={applicant.photo} 
                          alt={applicant.name}
                          className="w-24 h-24 rounded-full object-cover mx-auto lg:mx-0"
                        />
                      ) : (
                        <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto lg:mx-0">
                          <span className="text-gray-400 text-2xl">👤</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-grow">
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">{applicant.name}</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <span className="text-sm font-medium text-gray-500">Fundraising Goal:</span>
                          <p className="text-lg font-semibold text-green-600">${applicant.goal.toLocaleString()}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Application Date:</span>
                          <p className="text-gray-700">{new Date().toLocaleDateString()}</p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <span className="text-sm font-medium text-gray-500">Short Bio:</span>
                        <p className="text-gray-700 mt-1">{applicant.bio}</p>
                      </div>

                      {applicant.fullBio && applicant.fullBio !== applicant.bio && (
                        <div className="mb-4">
                          <span className="text-sm font-medium text-gray-500">Detailed Story:</span>
                          <p className="text-gray-700 mt-1">{applicant.fullBio}</p>
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row gap-3 mt-6">
                        <button
                          onClick={() => handleApprove(applicant.id)}
                          className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors font-semibold"
                        >
                          ✓ Approve Application
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm('Are you sure you want to reject this application? This action cannot be undone.')) {
                              // In a real app, you'd implement rejection logic
                              alert('Rejection functionality would be implemented here.');
                            }
                          }}
                          className="flex-1 bg-red-100 text-red-700 py-2 px-4 rounded-lg hover:bg-red-200 transition-colors font-semibold border border-red-300"
                        >
                          ✗ Reject
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

              <div className="mt-12 space-y-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-3">Admin Guidelines</h3>
                  <ul className="text-sm text-blue-700 space-y-2">
                    <li>• Review each applicant's bio and story for appropriate content</li>
                    <li>• Ensure fundraising goals are reasonable (typically $1,400)</li>
                    <li>• Verify that applicants understand the commitment involved</li>
                    <li>• Contact applicants directly if you have questions before approving</li>
                    <li>• Once approved, fundraising pages go live immediately</li>
                  </ul>
                </div>

                <div className="bg-purple-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-purple-800 mb-3">📦 Data Migration</h3>
                  <ul className="text-sm text-purple-700 space-y-2">
                    <li>• <strong>Migrate Data</strong> button transfers localStorage data to the backend server</li>
                    <li>• Useful when switching from local storage to centralized backend</li>
                    <li>• Creates automatic backup of existing backend data before migration</li>
                    <li>• Prevents duplicates - only adds users that don't already exist</li>
                    <li>• Safe to run multiple times - won't create duplicate entries</li>
                    <li>• After migration, localStorage can be safely cleared</li>
                  </ul>
                </div>
              </div>
            </>
          )}

          {/* Fundraisers Tab */}
          {activeTab === 'fundraisers' && (
            <FundraiserManagement />
          )}

          {/* Admins Tab */}
          {activeTab === 'admins' && (
            <AdminManagement />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;